import React, { useState } from 'react';
import { Form, Button, Row, Col, InputGroup, Alert } from 'react-bootstrap';
import { communityAssetTypes } from '../data/dummyData';
import { theme } from '../theme';

const AssetForm = ({ assetType, initialData: rawInitialData, onSubmit, onCancel }) => {
  // Guard: default parameter does NOT apply when null is passed explicitly.
  const initialData = rawInitialData || {};

  // Build form state safely without blindly spreading initialData.
  // The API response uses snake_case (owner_scope) but the form uses camelCase (ownerScope).
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    ownerScope: initialData.ownerScope || initialData.owner_scope || 'community',
    specs: initialData.specs || {},
    id: initialData.id || null,
  });

  const [errors, setErrors] = useState({});

  const assetConfig = communityAssetTypes[assetType];

  if (!assetConfig) {
    return <Alert variant="danger">Invalid asset type</Alert>;
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSpecChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      specs: {
        ...prev.specs,
        [field]: value,
      },
    }));
    // Clear error for this field
    if (errors[`specs.${field}`]) {
      setErrors(prev => ({ ...prev, [`specs.${field}`]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validate asset name
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Asset name is required';
    }

    // Validate required spec fields
    assetConfig.fields.forEach(field => {
      if (field.required && !formData.specs[field.name]) {
        newErrors[`specs.${field.name}`] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        type: assetType,
      });
    }
  };

  const renderField = (field) => {
    const value = formData.specs[field.name] ?? field.default ?? '';
    const error = errors[`specs.${field.name}`];

    switch (field.type) {
      case 'number':
        return (
          <Form.Group className="mb-3" key={field.name}>
            <Form.Label>
              {field.label} {field.required && <span className="text-danger">*</span>}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                value={value}
                onChange={(e) => handleSpecChange(field.name, parseFloat(e.target.value))}
                isInvalid={!!error}
                step="any"
              />
              {field.unit && <InputGroup.Text>{field.unit}</InputGroup.Text>}
              <Form.Control.Feedback type="invalid">
                {error}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        );

      case 'select':
        return (
          <Form.Group className="mb-3" key={field.name}>
            <Form.Label>
              {field.label} {field.required && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Select
              value={value}
              onChange={(e) => handleSpecChange(field.name, e.target.value)}
              isInvalid={!!error}
            >
              <option value="">Select {field.label}</option>
              {field.options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          </Form.Group>
        );

      case 'date':
        return (
          <Form.Group className="mb-3" key={field.name}>
            <Form.Label>
              {field.label} {field.required && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              type="date"
              value={value}
              onChange={(e) => handleSpecChange(field.name, e.target.value)}
              isInvalid={!!error}
            />
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          </Form.Group>
        );

      default:
        return (
          <Form.Group className="mb-3" key={field.name}>
            <Form.Label>
              {field.label} {field.required && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              type="text"
              value={value}
              onChange={(e) => handleSpecChange(field.name, e.target.value)}
              isInvalid={!!error}
            />
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          </Form.Group>
        );
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className="mb-4 p-3 rounded" style={{ backgroundColor: theme.colors.neutral[50] }}>
        <h5>
          <span className="me-2">{assetConfig.icon}</span>
          {assetConfig.label}
        </h5>
      </div>

      <Form.Group className="mb-3">
        <Form.Label>
          Asset Name <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          type="text"
          placeholder={`Enter ${assetConfig.label} name`}
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          isInvalid={!!errors.name}
        />
        <Form.Control.Feedback type="invalid">
          {errors.name}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Owner Scope</Form.Label>
        <Form.Select
          value={formData.ownerScope}
          onChange={(e) => handleChange('ownerScope', e.target.value)}
        >
          <option value="community">Community (Shared)</option>
          <option value="building">Building (Private)</option>
        </Form.Select>
        <Form.Text className="text-muted">
          Community assets are shared across all members
        </Form.Text>
      </Form.Group>

      <h6 className="mb-3">Technical Specifications</h6>
      <Row>
        {assetConfig.fields.map((field, index) => (
          <Col md={index % 2 === 0 ? 6 : 6} key={field.name}>
            {renderField(field)}
          </Col>
        ))}
      </Row>

      <div className="d-flex gap-2 justify-content-end mt-4">
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          style={{ backgroundColor: theme.colors.primary.main }}
        >
          {initialData.id ? 'Update Asset' : 'Add Asset'}
        </Button>
      </div>
    </Form>
  );
};

export default AssetForm;
